import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { FileText, Sparkle, FloppyDisk, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adminHeaders } from "@/lib/adminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LessonManager = () => {
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draftingAll, setDraftingAll] = useState(false);

  const fetchLessons = async () => {
    try {
      const res = await axios.get(`${API}/admin/lessons`, { headers: adminHeaders() });
      setLessons(res.data || []);
    } catch {
      toast.error("Failed to load lessons");
    }
  };

  const draftAllEmpty = async () => {
    const emptyCount = lessons.filter(l => !(l.content && l.content.trim())).length;
    if (emptyCount === 0) {
      toast.info("No empty lessons — every lesson already has content.");
      return;
    }
    if (!window.confirm(
      `This will use GPT-5.2 to draft ${emptyCount} empty lesson${emptyCount === 1 ? "" : "s"}. ` +
      `Takes ~2-3 minutes and consumes some LLM budget (~$0.03-$0.05/lesson). Continue?`
    )) return;

    setDraftingAll(true);
    toast.loading("Drafting all empty lessons…", { id: "draft-all" });
    try {
      const res = await axios.post(
        `${API}/admin/lessons/ai-draft-all`,
        {},
        { headers: adminHeaders(), timeout: 300000 } // 5 min
      );
      const { drafted_count, skipped_count, failed_count } = res.data;
      toast.success(
        `Drafted ${drafted_count} lesson${drafted_count === 1 ? "" : "s"}` +
          (skipped_count ? ` • Skipped ${skipped_count} with content` : "") +
          (failed_count ? ` • ${failed_count} failed` : ""),
        { id: "draft-all", duration: 6000 }
      );
      fetchLessons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI drafting failed", { id: "draft-all" });
    }
    setDraftingAll(false);
  };

  useEffect(() => {
    fetchLessons();
  }, []);

  if (editing) {
    return (
      <LessonEditor
        lessonId={editing}
        onClose={() => {
          setEditing(null);
          fetchLessons();
        }}
      />
    );
  }

  // Group by module
  const grouped = lessons.reduce((acc, l) => {
    (acc[l.module_id] = acc[l.module_id] || []).push(l);
    return acc;
  }, {});

  const emptyCount = lessons.filter(l => !(l.content && l.content.trim())).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <h1 className="font-fredoka font-bold text-2xl text-foreground">Lesson Content</h1>
          <p className="font-nunito text-muted-foreground text-sm">
            Click any lesson to edit • {emptyCount} of {lessons.length} still empty
          </p>
        </div>
        <Button
          onClick={draftAllEmpty}
          disabled={draftingAll || emptyCount === 0}
          className="bg-accent-purple hover:bg-purple-600 text-white font-nunito font-bold border-2 border-foreground shadow-brutal active-brutal rounded-full disabled:opacity-50"
          data-testid="admin-draft-all-lessons-btn"
        >
          <Sparkle size={16} weight="fill" className="mr-2" />
          {draftingAll ? "Drafting…" : `AI Draft All Empty (${emptyCount})`}
        </Button>
      </div>
      <p className="font-nunito text-muted-foreground text-xs mb-6">
        The <strong>AI Draft All Empty</strong> button fires GPT-5.2 in bulk — one editable draft per empty lesson. You still review + edit each one before students see it.
      </p>

      <div className="space-y-6">
        {Object.entries(grouped).map(([mid, items]) => (
          <div key={mid}>
            <h2 className="font-fredoka font-bold text-lg text-foreground mb-2">
              Module {mid}
            </h2>
            <div className="grid gap-2">
              {items.map((l) => (
                <button
                  key={l.lesson_id}
                  onClick={() => setEditing(l.lesson_id)}
                  className="bg-white border-2 border-foreground rounded-xl shadow-brutal-sm p-4 flex items-center gap-3 text-left hover:bg-muted/20 transition-colors"
                  data-testid={`admin-edit-lesson-${l.lesson_id}`}
                >
                  <FileText size={22} weight="duotone" className="text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-nunito font-bold text-foreground">{l.title}</p>
                    <p className="text-xs font-nunito text-muted-foreground">
                      {l.content ? `${l.content.length} chars` : "Empty — needs content"}
                      {l.video_url ? " • 🎥 video" : ""}
                      {l.updated_at ? ` • updated ${new Date(l.updated_at).toLocaleDateString()}` : ""}
                    </p>
                  </div>
                  <span className="text-xs font-nunito font-bold px-2 py-0.5 rounded-full border-2 border-foreground/30">
                    {l.lesson_id}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LessonEditor = ({ lessonId, onClose }) => {
  const [lesson, setLesson] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/lesson/${lessonId}`, { headers: adminHeaders() })
      .then((r) => setLesson(r.data))
      .catch(() => toast.error("Failed to load"));
  }, [lessonId]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/lesson/${lessonId}`,
        {
          title: lesson.title,
          content: lesson.content,
          video_url: lesson.video_url,
        },
        { headers: adminHeaders() }
      );
      setLesson(res.data);
      toast.success("Saved!");
    } catch {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const aiDraft = async () => {
    if (lesson.content && !window.confirm("This will overwrite the current content. Continue?")) return;
    setDrafting(true);
    try {
      const res = await axios.post(
        `${API}/admin/lessons/ai-draft/${lessonId}`,
        {},
        { headers: adminHeaders() }
      );
      setLesson(res.data);
      toast.success("AI draft created — review and edit!");
    } catch {
      toast.error("Draft failed");
    }
    setDrafting(false);
  };

  if (!lesson) {
    return (
      <div className="text-center py-12">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div>
      <Button
        variant="outline"
        onClick={onClose}
        className="mb-4 font-nunito border-2 border-foreground rounded-full"
        data-testid="admin-lesson-back"
      >
        <ArrowLeft size={16} weight="bold" className="mr-2" />
        Back to all lessons
      </Button>

      <div className="bg-white border-2 border-foreground rounded-2xl shadow-brutal-sm p-6 mb-4">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-fredoka font-bold text-xl text-foreground">
            Lesson {lesson.lesson_id}
          </h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPreview(!preview)}
              className="font-nunito border-2 border-foreground rounded-full"
            >
              {preview ? "Edit" : "Preview"}
            </Button>
            <Button
              onClick={aiDraft}
              disabled={drafting}
              className="bg-accent-purple hover:bg-purple-600 text-white font-nunito font-bold border-2 border-foreground shadow-brutal-sm rounded-full"
              data-testid="admin-ai-draft-btn"
            >
              <Sparkle size={16} weight="fill" className="mr-2" />
              {drafting ? "Drafting..." : "AI Draft"}
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal-sm rounded-full"
              data-testid="admin-lesson-save"
            >
              <FloppyDisk size={16} weight="fill" className="mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        <label className="block font-nunito font-bold text-sm mb-1">Title</label>
        <Input
          value={lesson.title}
          onChange={(e) => setLesson({ ...lesson, title: e.target.value })}
          className="font-nunito border-2 border-foreground rounded-xl mb-4"
          data-testid="admin-lesson-title"
        />

        <label className="block font-nunito font-bold text-sm mb-1">
          Video URL (YouTube unlisted, optional)
        </label>
        <Input
          value={lesson.video_url || ""}
          onChange={(e) => setLesson({ ...lesson, video_url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=..."
          className="font-nunito border-2 border-foreground rounded-xl mb-4"
          data-testid="admin-lesson-video"
        />

        <label className="block font-nunito font-bold text-sm mb-1">
          Content (Markdown supported)
        </label>
        {preview ? (
          <div className="prose prose-sm max-w-none border-2 border-foreground/20 rounded-xl p-4 bg-muted/10 font-nunito min-h-[400px]">
            <ReactMarkdown>{lesson.content || "*Empty*"}</ReactMarkdown>
          </div>
        ) : (
          <Textarea
            value={lesson.content || ""}
            onChange={(e) => setLesson({ ...lesson, content: e.target.value })}
            className="font-mono text-sm border-2 border-foreground rounded-xl min-h-[400px]"
            placeholder="## Overview&#10;&#10;Start writing your lesson here..."
            data-testid="admin-lesson-content"
          />
        )}
      </div>
    </div>
  );
};

export default LessonManager;
